import { useState } from 'react';
import { Modal, NewBaseTable } from 'components';
import { ColumnConfig } from 'components/NewBaseTable/NewBaseTable';
import { useTranslation } from 'react-i18next';
import { generateIcon } from './utils';
import { formatDuration } from 'common/helpers/v1/dates';
import { ProgrammedNestedStepsContainer } from './elements';
import ExternalLink from 'icons/indexed-icons/ExternalLink';
import ProcessLogModal from './ProcessLogModal';
import { useProcessLogDetailRunIdWidget } from './utils/useProcessLogDetailAPIPovider';
import { SessionAlarmsFlyout } from '../Flyouts/SessionAlarmsFlyout/SessionAlarmsFlyout';
import { Alarm } from './StepsTable';
import { convertToZonedTimestamp } from 'common/helpers/dateAndTimeHelpersV2';
import { useMachineInfo } from '../../../../../../providers';
export interface Props {
  data?: any[];
}

export const NestedStepsTable = ({ data }: Props): JSX.Element => {
  const { t } = useTranslation(['mh']);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [runlogId, setRunlogId] = useState<string | null>(null);
  // Fetch process log data for the modal
  const { isLoading, processData, machineId } = useProcessLogDetailRunIdWidget(runlogId || '');
  const { timeZone } = useMachineInfo();
  const handleClick = (id: string) => {
    if (id) {
      setRunlogId(id);
      setShowModal(true);
    }
  };
  const [selectedGroupAlarms, setSelectedGroupAlarms] = useState<Alarm[] | null>(null);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const handleAlarmsClick = (alarms: Alarm[]): void => {
    setSelectedGroupAlarms(alarms);
    setIsFlyoutOpen(true);
  };

  const generateColumnConfigsSubStepsTable = (): ColumnConfig[] => {
    return [
      // Third-Level Columns
      {
        id: 'id',
        header: t('Run ID') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ row }) => {
          const startTime = row.original.startTime?.split('.')[0];
          const startDate = convertToZonedTimestamp(
            startTime as string,
            timeZone,
            'do MMM - HH:mm:ss'
          );
          return (
            <>
              <span className="text-bold">{row.original.id}</span>&nbsp;({startDate})
            </>
          );
        }
      },
      {
        id: 'status',
        header: t('Status') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ row }) => {
          const { sterilizationResult, mainName, active } = row.original;
          // Determine status and icon
          const getStatusAndIcon = () => {
            if (mainName === 'Sterilisation' || mainName === 'Sterilization') {
              if (active) {
                // When active is true, show "In Progress"
                return { statusText: 'In Progress', icon: generateIcon('ongoing') };
              } else {
                // When active is false, derive from sterilizationResult
                switch (sterilizationResult) {
                  case 'Completed':
                    return { statusText: 'Completed', icon: generateIcon('green checkmark') };
                  case 'In Progress':
                    return { statusText: 'In Progress', icon: generateIcon('ongoing') };
                  case 'Not Completed':
                    return { statusText: 'Not Completed', icon: generateIcon('in progress') };
                  default:
                    return { statusText: 'No Status', icon: generateIcon('in progress') }; // Handle unexpected values
                }
              }
            } else {
              // Fallback for non-sterilization rows
              return active
                ? { statusText: 'In Progress', icon: generateIcon('ongoing') }
                : { statusText: 'Completed', icon: generateIcon('green checkmark') };
            }
          };
          const { statusText, icon } = getStatusAndIcon();
          return (
            <div className="cell-status">
              {icon && <span>{icon}</span>}&nbsp;{statusText}
            </div>
          );
        }
      },
      {
        id: 'alarms',
        header: 'Alarm',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ row }) => (
          <div className="cell-status">
            {row.original.alarms?.length > 0 ? (
              <>
                {generateIcon('bug')}
                &nbsp; Alarms <b>({row.original.alarms?.length})</b>
                <button
                  className="button-issues"
                  onClick={() => handleAlarmsClick(row.original.alarms as Alarm[])}
                >
                  {generateIcon('blue arrow')}
                </button>
              </>
            ) : (
              <>{''}</>
            )}
          </div>
        )
      },
      {
        id: 'duration',
        header: t('Duration') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ getValue }) => {
          return <>{getValue()} </>;
        }
      },
      {
        id: 'startTime',
        header: t('Target Duration') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ row }) => {
          const totalDiff = formatDuration(row.original.startTime, 'hours:mins:secs');
          return (
            <>
              <p>{totalDiff}</p>
            </>
          );
        }
      },
      {
        id: 'processLog',
        header: data.some(
          (row) =>
            row.mainName === 'Cleaning' ||
            row.mainName === 'Sterilisation' ||
            row.mainName === 'Sterilization'
        )
          ? (t('Process log') as string)
          : '',
        isEnableSorting: false,
        isSelected: true,
        renderer: ({ row }) => {
          const { mainName, id } = row.original;
          // Only show the process log icon if parentName is "Cleaning" or "Sterilisation" or "Sterilization"
          if (
            mainName === 'Cleaning' ||
            mainName === 'Sterilisation' ||
            mainName === 'Sterilization'
          ) {
            return (
              <div className="cell-status">
                <button className="button-issues" onClick={() => handleClick(id as string)}>
                  <ExternalLink />
                </button>
              </div>
            );
          }
          // Return empty if the condition is not met
          return null;
        }
      }
    ];
  };

  return (
    <>
      <ProgrammedNestedStepsContainer className="changeover-single-session--program-stepsnested">
        <NewBaseTable
          newTableData={data} // Passing subRows to the table
          customClassName="nested-row"
          textAlign="left"
          tdMarginLeft="0.625rem"
          sortState={{ id: 'id', desc: false }} // sort by name in descending order by default
          isShowColumnOptions={false}
          isShowGlobalSearch={false}
          disableVirtualize={true}
          hideTotalFetchCount={true}
          columnConfigs={generateColumnConfigsSubStepsTable()}
        />
      </ProgrammedNestedStepsContainer>

      <Modal visible={showModal} onClose={() => setShowModal(false)}>
        <ProcessLogModal
          active={showModal}
          handleClose={() => setShowModal(false)}
          isLoading={isLoading}
          processData={processData}
          processOrder={processData}
          machineId={machineId}
        />
      </Modal>
      {isFlyoutOpen && (
        <SessionAlarmsFlyout
          data={selectedGroupAlarms}
          show={isFlyoutOpen}
          setShow={setIsFlyoutOpen}
        />
      )}
    </>
  );
};

export default NestedStepsTable;
